import { CheckBox } from '@mui/icons-material'
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import { ethers } from 'ethers'
import { useState } from 'react'

interface ContractDetailsDialogProps {
    open: boolean
    onClose: () => void
    contract?: any
}

const NFTButtons = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Button disabled>Populate configs from provider NFT</Button>
        <Button disabled>Publish configs as provider NFT</Button>
    </Box>
)

const GeneralConfigurations = ({ companyName }: { companyName: string }) => (
    <Stack spacing={2}>
        <Typography variant="h6">General Configurations</Typography>
        <TextField label="Company Name" value={companyName} disabled />
        <TextField label="Last 4 of TIN(s) CSV" value="To be implemented" disabled />
        <TextField
            label="Configuration saved in NFT address"
            value="Change to smart contract address?"
            disabled
        />
    </Stack>
)

const ProviderTab = (props: ContractDetailsDialogProps) => (
    <Grid container spacing={2}>
        <Grid item xs={12}>
            <NFTButtons />
        </Grid>
        <Grid item xs={12}>
            <Divider />
        </Grid>
        <Grid item xs={6}>
            <GeneralConfigurations companyName={props.contract?.receiverOrg.companyName ?? '--'} />
        </Grid>
        <Grid item xs={6}>
            <Stack spacing={2}>
                <Typography variant="h6">Provider NPI Number(s)</Typography>
                <Typography>To do</Typography>
            </Stack>
        </Grid>
        <Grid item xs={12}>
            <Divider />
        </Grid>
        <Grid item xs={12}>
            <Typography>Provider ETH Addresses</Typography>
            <Typography>
                These are the addresses that can execute the Contract, and where funds are sent
                should the claim be processed successfully.
            </Typography>
        </Grid>
        <Grid item xs={8}>
            <TextField sx={{ width: '100%' }} />
        </Grid>
        <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'end' }}>
            <Button>Add</Button>
        </Grid>
        <Grid item xs={8}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    marginLeft: 1,
                }}
            >
                {props.contract?.receiverAddresses
                    .filter((address: any) => address[0] !== ethers.constants.AddressZero)
                    .map((address: any) => (
                        <FormControlLabel
                            key={address[0]}
                            label={address[0]?.substring(0, 8).concat('...')}
                            labelPlacement="end"
                            disabled
                            checked={false}
                            control={<CheckBox />}
                        />
                    ))}
            </Box>
        </Grid>
        <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'end' }}>
            <Button>Remove selected</Button>
        </Grid>
        <Grid item xs={12}>
            <Divider />
        </Grid>
        <Grid item xs={12}>
            <Typography>Provider EDI Number(s)</Typography>
        </Grid>
        <Grid item xs={8}>
            <TextField sx={{ width: '100%' }} />
        </Grid>
        <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'end' }}>
            <Button>Add</Button>
        </Grid>
        <Grid item xs={8}>
            <Stack spacing={2}>
                {props.contract?.receiverOrg.ediNumbers?.map((ediNum: any) => (
                    <FormControlLabel
                        key={ediNum}
                        label={ediNum}
                        labelPlacement="end"
                        disabled
                        control={<CheckBox />}
                    />
                ))}
            </Stack>
        </Grid>
        <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'end' }}>
            <Button>Remove selected</Button>
        </Grid>
        <Grid item xs={12}>
            <Divider />
        </Grid>
        <Grid item xs={12}>
            <Typography>Service Address</Typography>
        </Grid>
        <Grid item xs={5}>
            <TextField sx={{ width: '100%' }} label="Line 1" disabled />
        </Grid>
        <Grid item xs={4}>
            <TextField sx={{ width: '100%' }} label="Line 2" disabled />
        </Grid>
        <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'end' }}>
            <Button>Add</Button>
        </Grid>
        <Grid item xs={3}>
            <TextField sx={{ width: '100%' }} label="City" disabled />
        </Grid>
        <Grid item xs={3}>
            <TextField sx={{ width: '100%' }} label="State" disabled />
        </Grid>
        <Grid item xs={3}>
            <TextField sx={{ width: '100%' }} label="Zip Code" disabled />
        </Grid>
        <Grid item xs={3} />
    </Grid>
)

const PayerTab = (props: ContractDetailsDialogProps) => (
    <Grid container spacing={2}>
        <Grid item xs={12}>
            <NFTButtons />
        </Grid>
        <Grid item xs={12}>
            <Divider />
        </Grid>
        <Grid item xs={6}>
            <GeneralConfigurations companyName={props.contract?.senderOrg.companyName ?? '--'} />
        </Grid>
        <Grid item xs={6}>
            <Stack spacing={2}>
                <Typography variant="h6">Contract Author</Typography>
                <TextField label="Name" sx={{ width: '100%' }} disabled />
                <TextField label="Address" sx={{ width: '100%' }} disabled />
                <Typography variant="h6">Contract Support</Typography>
                <TextField label="Email" sx={{ width: '100%' }} disabled />
                <TextField label="Phone" sx={{ width: '100%' }} disabled />
            </Stack>
        </Grid>
    </Grid>
)

const DetailsTab = (props: ContractDetailsDialogProps) => (
    <Grid container spacing={2}>
        <Grid item xs={12}>
            <Typography>TODO: Medicare</Typography>
        </Grid>
        <Grid item xs={6}>
            <Stack spacing={2}>
                <TextField
                    label="Escrow Account Address"
                    sx={{ width: '100%' }}
                    disabled
                    value={props.contract?.ethAddress}
                />
                <TextField
                    label="Escrow Duration (Days)"
                    sx={{ width: '100%' }}
                    disabled
                    value={props.contract?.lockedFor}
                />
                <TextField
                    label="Funds in Escrow"
                    sx={{ width: '100%' }}
                    disabled
                    value={props.contract?.funds.amountInEscrow}
                />
            </Stack>
        </Grid>
        <Grid item xs={6}>
            <Stack spacing={2}>
                <TextField
                    label="Funds Available in Contract"
                    sx={{ width: '100%' }}
                    disabled
                    value={props.contract?.funds.freeFunds}
                />
                <TextField label="Execution Fee (Payer)" sx={{ width: '100%' }} disabled />
                <TextField label="Execution Fee (Rhiza)" sx={{ width: '100%' }} disabled />
            </Stack>
        </Grid>
        <Grid item xs={12}>
            <Divider />
        </Grid>
        <Grid item xs={12}>
            <Typography variant="h6">CPT Code</Typography>
        </Grid>
        <Grid item xs={12}>
            <Divider />
        </Grid>
        <Grid item xs={12}>
            <Typography>TODO</Typography>
        </Grid>
        <Grid item xs={12}>
            <Stack spacing={2}>
                <TextField
                    label="Terms and Conditions"
                    sx={{ width: '100%' }}
                    disabled
                    value={props.contract?.termsAndCondHash}
                />
                <TextField label="Version" sx={{ width: '100%' }} disabled />
                <TextField
                    label="Terms and Conditions Hash"
                    sx={{ width: '100%' }}
                    disabled
                    value={props.contract?.termsAndCondHash}
                />
            </Stack>
        </Grid>
    </Grid>
)

const ContractDetailsDialog = (props: ContractDetailsDialogProps) => {
    const [selectedTab, setSelectedTab] = useState('provider')

    const renderTabView = () => {
        switch (selectedTab) {
            case 'provider':
                return <ProviderTab {...props} />
            case 'payer':
                return <PayerTab {...props} />
            case 'details':
                return <DetailsTab {...props} />
        }
    }
    if (!props.contract) return <></>
    return (
        <Dialog open={props.open} maxWidth="lg">
            <DialogTitle>Details</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ padding: 1 }}>
                    <Grid item xs={6}>
                        <TextField sx={{ width: '100%' }} value={props.contract.name} disabled />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            label="Name"
                            sx={{ width: '100%' }}
                            value={props.contract.name}
                            disabled
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <DatePicker
                            label="Start"
                            sx={{ width: '100%' }}
                            value={dayjs(props.contract.startDate)}
                            disabled
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <DatePicker
                            label="End"
                            sx={{ width: '100%' }}
                            value={dayjs(props.contract.endDate)}
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Status"
                            sx={{ width: '100%' }}
                            value={props.contract.status}
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Description"
                            sx={{ width: '100%' }}
                            value="Contract description"
                            disabled
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                            <Button onClick={props.onClose}>Cancel</Button>
                            <Button disabled>Save as Draft</Button>
                            <Button disabled>Verify</Button>
                            <Button disabled>Publish</Button>
                        </Box>
                    </Grid>
                </Grid>
                <Tabs value={selectedTab} onChange={(_event, value) => setSelectedTab(value)}>
                    <Tab label="Provider" value="provider" />
                    <Tab label="Payer" value="payer" />
                    <Tab label="Details" value="details" />
                </Tabs>
                <Box sx={{ paddingTop: 2 }}>{renderTabView()}</Box>
            </DialogContent>
        </Dialog>
    )
}

export default ContractDetailsDialog
